/**
 * AES-GCM encryption for IndexedDB cached data.
 *
 * The encryption key is derived from the user's password via PBKDF2 at login time
 * and held in module-scope memory (never persisted). When the app restarts without
 * password entry (e.g. biometric unlock), the key is unavailable and cached data
 * remains encrypted — this is the intended BEP behavior.
 *
 * Uses Web Crypto API (crypto.subtle) — available in all modern browsers.
 */

const PBKDF2_ITERATIONS = 100_000;
const KEY_LENGTH = 256;
const IV_LENGTH = 12; // 96-bit IV for AES-GCM

/** Encryption key held in memory only — cleared on logout or page close */
let _encryptionKey: CryptoKey | null = null;

/**
 * Derive an AES-GCM key from a password and salt using PBKDF2.
 */
export async function deriveEncryptionKey(
  password: string,
  salt: Uint8Array,
): Promise<CryptoKey> {
  const encoder = new TextEncoder();
  const encoded = encoder.encode(password);
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    encoded.buffer as ArrayBuffer,
    'PBKDF2',
    false,
    ['deriveKey'],
  );

  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: salt.buffer as ArrayBuffer,
      iterations: PBKDF2_ITERATIONS,
      hash: 'SHA-256',
    },
    keyMaterial,
    { name: 'AES-GCM', length: KEY_LENGTH },
    false, // non-extractable
    ['encrypt', 'decrypt'],
  );
}

/**
 * Store the derived encryption key in module-scope memory.
 */
export function setEncryptionKey(key: CryptoKey): void {
  _encryptionKey = key;
}

/**
 * Get the current encryption key (null if not available).
 */
export function getEncryptionKey(): CryptoKey | null {
  return _encryptionKey;
}

/**
 * Clear the encryption key from memory (on logout).
 */
export function clearEncryptionKey(): void {
  _encryptionKey = null;
}

/**
 * Check if encryption is available (key has been derived from password).
 */
export function isEncryptionAvailable(): boolean {
  return _encryptionKey !== null;
}

export interface EncryptedData {
  /** Base64-encoded initialization vector */
  iv: string;
  /** Base64-encoded ciphertext */
  ciphertext: string;
}

/**
 * Encrypt a plaintext string using AES-GCM.
 * Throws if no encryption key is available.
 */
export async function encrypt(plaintext: string): Promise<EncryptedData> {
  if (!_encryptionKey) throw new Error('Encryption key not available');

  const encoder = new TextEncoder();
  const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));

  const encoded = encoder.encode(plaintext);
  const ciphertext = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv: iv.buffer as ArrayBuffer },
    _encryptionKey,
    encoded.buffer as ArrayBuffer,
  );

  return {
    iv: arrayBufferToBase64(iv),
    ciphertext: arrayBufferToBase64(ciphertext),
  };
}

/**
 * Decrypt an encrypted data object back to plaintext.
 * Throws if no encryption key is available or decryption fails.
 */
export async function decrypt(data: EncryptedData): Promise<string> {
  if (!_encryptionKey) throw new Error('Encryption key not available');

  const iv = base64ToUint8Array(data.iv);
  const ciphertext = base64ToUint8Array(data.ciphertext);

  const plaintext = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: iv.buffer as ArrayBuffer },
    _encryptionKey,
    ciphertext.buffer as ArrayBuffer,
  );

  return new TextDecoder().decode(plaintext);
}

function arrayBufferToBase64(buffer: ArrayBuffer | Uint8Array): string {
  const bytes = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
  let binary = '';
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary);
}

function base64ToUint8Array(base64: string): Uint8Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}
